import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send, Loader2, Instagram, Youtube, Facebook } from "lucide-react";
import { Reveal } from "./Reveal";
import PayDeposit from "./PayDeposit";
import { CONTACT, DESTINATIONS } from "@/data/content";
import { api } from "@/lib/api";

const contactRows = [
  {
    icon: Phone,
    label: "Call us — UAE",
    value: CONTACT.phoneDisplay,
    href: `tel:${CONTACT.phoneRaw}`,
    testid: "contact-call-link",
  },
  {
    icon: Phone,
    label: "Call us — India",
    value: CONTACT.phone2Display,
    href: `tel:${CONTACT.phone2Raw}`,
    testid: "contact-call-link-india",
  },
  {
    icon: Phone,
    label: "Call us — UAE (2)",
    value: CONTACT.phone3Display,
    href: `tel:${CONTACT.phone3Raw}`,
    testid: "contact-call-link-uae-2",
  },
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: CONTACT.phoneDisplay,
    href: CONTACT.whatsapp,
    testid: "contact-whatsapp-link",
  },
  {
    icon: Mail,
    label: "Email",
    value: CONTACT.email,
    href: `mailto:${CONTACT.email}`,
    testid: "contact-email-link",
  },
  {
    icon: Instagram,
    label: "Instagram",
    value: "@rktousrismandtravel",
    href: CONTACT.instagram,
    testid: "contact-instagram-link",
  },
  {
    icon: Youtube,
    label: "YouTube",
    value: "@RkTourismAndTravel",
    href: CONTACT.youtube,
    testid: "contact-youtube-link",
  },
  {
    icon: Facebook,
    label: "Facebook",
    value: "RK Tourism & Travels",
    href: CONTACT.facebook,
    testid: "contact-facebook-link",
  },
  {
    icon: MapPin,
    label: "Location",
    value: CONTACT.location,
    href: null,
    testid: "contact-location",
  },
];

const inputCls =
  "w-full bg-[#0A1128] border border-[#d4af37]/25 px-4 py-3.5 text-slate-50 placeholder:text-slate-400 focus:outline-none focus:border-[#d4af37] transition-colors duration-300";

export default function Booking() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", destination: "", travel_date: "", message: "" });
  const [sending, setSending] = useState(false);
  const [lastEnquiry, setLastEnquiry] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setSending(true);
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v.trim() !== ""));
    try {
      const { data } = await api.post("/enquiries", payload);
      setLastEnquiry(data);
      toast.success("Request received — we'll be in touch shortly.");
    } catch {
      toast.error("Couldn't save your request, but WhatsApp is opening so we still get it.");
    }
    const text = `Hello RK Tourism & Travel! I'm ${form.name}. I'm interested in ${form.destination || "a tour"}${form.travel_date ? ` around ${form.travel_date}` : ""}.${form.message ? ` ${form.message}` : ""} (Phone: ${form.phone})`;
    window.open(`${CONTACT.whatsapp}?text=${encodeURIComponent(text)}`, "_blank");
    setForm({ name: "", phone: "", email: "", destination: "", travel_date: "", message: "" });
    setSending(false);
  };

  return (
    <section id="booking" data-testid="booking-section" className="py-24 md:py-32 bg-[#101C3E]/30 border-t border-[#d4af37]/15">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <div id="contact">
            <p className="text-xs uppercase tracking-[0.35em] text-[#d4af37] font-medium mb-6">
              Begin Your Journey
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-tight text-slate-50">
              The world is waiting.{" "}
              <span className="italic text-[#d4af37]">Let's talk.</span>
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed max-w-md">
              Tell us where your heart is pointing and we'll draft a route —
              with visas, flights and stays handled from our UAE office.
            </p>
            <div className="mt-10 space-y-2">
              {contactRows.map((row) => {
                const Icon = row.icon;
                const inner = (
                  <>
                    <span className="w-11 h-11 border border-[#d4af37]/40 flex items-center justify-center shrink-0 group-hover:bg-[#d4af37] transition-colors duration-300">
                      <Icon size={17} className="text-[#d4af37] group-hover:text-[#0A1128] transition-colors duration-300" />
                    </span>
                    <span>
                      <span className="block text-[10px] uppercase tracking-[0.25em] text-slate-400">
                        {row.label}
                      </span>
                      <span className="block text-slate-50 mt-0.5">{row.value}</span>
                    </span>
                  </>
                );
                return row.href ? (
                  <a
                    key={row.label}
                    href={row.href}
                    data-testid={row.testid}
                    target={row.href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    className="group flex items-center gap-5 py-3"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={row.label} data-testid={row.testid} className="group flex items-center gap-5 py-3">
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <form
            data-testid="booking-form"
            onSubmit={handleSubmit}
            className="backdrop-blur-xl bg-[#101C3E]/60 border border-[#d4af37]/25 p-8 md:p-10 space-y-5"
          >
            <h3 className="font-display text-2xl md:text-3xl font-light text-slate-50 mb-2">
              Request a booking
            </h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <input
                data-testid="booking-name-input"
                className={inputCls}
                placeholder="Your name *"
                value={form.name}
                onChange={set("name")}
              />
              <input
                data-testid="booking-phone-input"
                className={inputCls}
                placeholder="Phone / WhatsApp *"
                value={form.phone}
                onChange={set("phone")}
              />
            </div>
            <input
              data-testid="booking-email-input"
              type="email"
              className={inputCls}
              placeholder="Email (optional)"
              value={form.email}
              onChange={set("email")}
            />
            <div className="grid sm:grid-cols-2 gap-5">
              <select
                data-testid="booking-destination-select"
                className={`${inputCls} appearance-none ${form.destination ? "" : "text-slate-400"}`}
                value={form.destination}
                onChange={set("destination")}
              >
                <option value="" className="bg-[#0A1128] text-slate-50">Choose a destination</option>
                {DESTINATIONS.map((d) => (
                  <option key={d.name} value={d.name} className="bg-[#0A1128] text-slate-50">
                    {d.name}
                  </option>
                ))}
                <option value="Not sure yet" className="bg-[#0A1128] text-slate-50">
                  Not sure yet
                </option>
              </select>
              <input
                data-testid="booking-date-input"
                type="date"
                className={`${inputCls} ${form.travel_date ? "" : "text-slate-400"}`}
                value={form.travel_date}
                onChange={set("travel_date")}
              />
            </div>
            <textarea
              data-testid="booking-message-input"
              rows={4}
              className={`${inputCls} resize-none`}
              placeholder="Tell us about your trip — travellers, budget, must-sees…"
              value={form.message}
              onChange={set("message")}
            />
            <button
              data-testid="booking-submit-button"
              type="submit"
              disabled={sending}
              className="group w-full flex items-center justify-center gap-3 bg-[#d4af37] text-[#0A1128] px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium transition-colors duration-300 hover:bg-[#f3e5ab] disabled:opacity-60"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {sending ? "Sending…" : "Send Request & Open WhatsApp"}
            </button>
            <p className="text-xs text-slate-400 text-center">
              Your request is saved and WhatsApp opens so we can reply faster.
            </p>
          </form>

          {lastEnquiry && (
            <div
              data-testid="deposit-panel"
              className="mt-6 backdrop-blur-xl bg-[#101C3E]/60 border border-[#d4af37]/40 p-6 md:p-8 text-center"
            >
              <p className="font-display text-2xl text-slate-50">
                Reserve your slot, <span className="italic text-[#d4af37]">{lastEnquiry.name.split(" ")[0]}.</span>
              </p>
              <p className="text-sm text-slate-400 mt-2 mb-6">
                Secure your booking request with a small advance — fully adjustable against your final package.
              </p>
              <PayDeposit enquiry={lastEnquiry} />
              <p className="text-xs text-slate-400 mt-4">
                Prefer to pay later? No problem — we confirm every detail on WhatsApp first.
              </p>
            </div>
          )}
        </Reveal>
      </div>

      <Reveal className="max-w-7xl mx-auto px-6 mt-16">
        <div className="border border-[#d4af37]/25 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
          <iframe
            title="RK Tourism & Travels office — Twin Towers, Al Qusais-3, Dubai"
            data-testid="office-map"
            src={CONTACT.mapEmbed}
            className="w-full h-[380px] block border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-5">
          <p className="flex items-center gap-3 text-slate-300" data-testid="office-address">
            <span className="w-9 h-9 border border-[#d4af37]/40 flex items-center justify-center shrink-0">
              <MapPin size={15} className="text-[#d4af37]" />
            </span>
            {CONTACT.location}
          </p>
          <a
            href={CONTACT.mapLink}
            target="_blank"
            rel="noreferrer"
            data-testid="get-directions-link"
            className="inline-flex items-center justify-center border border-[#d4af37] text-[#d4af37] px-6 py-3 text-xs tracking-[0.15em] uppercase transition-colors duration-300 hover:bg-[#d4af37] hover:text-[#0A1128]"
          >
            Get Directions
          </a>
        </div>
      </Reveal>
    </section>
  );
}
